import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { HelpCircle } from 'lucide-react'
import './CognitiveLogs.scss'
import Sidebar from '../../components/Sidebar/Sidebar'
import { drumtaleAdminSessionAtom } from '../../AtomStore'
import { openPageSpecification } from '../shared/pageSpecification'
import {
  CognitiveContent,
  CognitiveReport,
  cognitiveLogsDataModelGuide,
  cognitiveLogsImplementationGuide,
  cognitiveLogsSpecification,
} from './cognitiveData'

const TOAST_DURATION_MS = 2400

function CognitiveLogs() {
  const navigate = useNavigate()
  const isAdminSessionActive = useAtomValue(drumtaleAdminSessionAtom)

  const [periodStart, setPeriodStart] = useState('2026-07-01')
  const [periodEnd, setPeriodEnd] = useState('2026-07-28')

  const cognitive: CognitiveContent[] = []
  const cognitiveReports: CognitiveReport[] = []

  const [toastMessage, setToastMessage] = useState('')
  const [isToastVisible, setIsToastVisible] = useState(false)
  const toastTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isAdminSessionActive) {
      navigate('/')
    }
  }, [isAdminSessionActive, navigate])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current)
      }
    }
  }, [])

  const showToast = (message: string) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current)
    }
    setToastMessage(message)
    setIsToastVisible(true)
    toastTimerRef.current = window.setTimeout(() => setIsToastVisible(false), TOAST_DURATION_MS)
  }

  const handleOpenSpecification = () => {
    const opened = openPageSpecification(cognitiveLogsSpecification, cognitiveLogsDataModelGuide, cognitiveLogsImplementationGuide)
    if (!opened) {
      showToast('화면 사양서 창을 열 수 없습니다. 팝업 차단을 해제해 주세요.')
    }
  }

  return (
    <div className='CognitiveLogs'>
      <div className='app-shell'>
        <Sidebar />

        <main className='workspace'>
          <header className='topbar'>
            <div className='title-area'>
              <h1>인지로그</h1>
            </div>
          </header>

          <section className='view active'>
            <div className='split-layout'>
              <section className='panel'>
                <div className='panel-header'>
                  <div>
                    <h2>콘텐츠별 이용현황</h2>
                    <p>이번 달 기준입니다.</p>
                  </div>
                  <div className='filter-group'>
                    <div className='period-range'>
                      <select className='filter period-filter' defaultValue='month'>
                        <option value='day'>일간</option>
                        <option value='week'>주간</option>
                        <option value='month'>월간</option>
                      </select>
                      <input className='date-filter' type='date' value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} />
                      <span>~</span>
                      <input className='date-filter' type='date' value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} />
                    </div>
                    <select className='compact-input'>
                      <option>인기순</option>
                      <option>최신순</option>
                    </select>
                  </div>
                </div>
                <div className='analytics-board compact'>
                  {cognitive.map((item) => (
                    <article className='config-card analytics-card' key={item.title}>
                      <h3>{item.title}</h3>
                      <p>{item.category}</p>
                      <div className='level-row'>
                        <span>플레이</span>
                        <div className='progress'><span style={{ width: `${Math.min(100, item.plays / 20)}%` }} /></div>
                        <strong>{item.plays.toLocaleString()}</strong>
                      </div>
                      <div className='level-row'>
                        <span>리포트</span>
                        <div className='progress'><span style={{ width: `${Math.min(100, item.reports / 12)}%` }} /></div>
                        <strong>{item.reports.toLocaleString()}</strong>
                      </div>
                    </article>
                  ))}
                </div>
              </section>

              <section className='panel'>
                <div className='panel-header'>
                  <div>
                    <h2>인지 리포트</h2>
                    <p>관리자가 열람 가능한 최근 생성 리포트입니다.</p>
                  </div>
                  <button className='ghost-button' type='button' onClick={() => showToast('인지 리포트 전체 보기는 추후 제공됩니다.')}>전체 보기</button>
                </div>
                <div className='report-list'>
                  {cognitiveReports.map((report) => (
                    <div className='report-item' key={`${report.member}-${report.title}`}>
                      <div>
                        <strong>{report.member} · {report.title}</strong>
                        <small>{report.summary}</small>
                      </div>
                      <div className='button-row'>
                        <span>{report.date}</span>
                        <button className='secondary-button' type='button' onClick={() => showToast(`${report.member} 리포트 상세는 추후 제공됩니다.`)}>보기</button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </section>
        </main>
      </div>

      <button
        className='page-spec-button'
        type='button'
        aria-label='현재 화면 사양서 열기'
        title='현재 화면 사양서'
        onClick={handleOpenSpecification}
      >
        <HelpCircle size={22} />
      </button>

      <div className={`toast${isToastVisible ? ' show' : ''}`} role='status' aria-live='polite'>
        {toastMessage}
      </div>
    </div>
  )
}

export default CognitiveLogs
